import React, {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {css, Global} from '@emotion/react'
import styled from '@emotion/styled'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import minus from '@vio/atlas/utils/minus'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {BrandLogo} from '../../BrandLogo/BrandLogo'
import {ChevronDownIcon} from './assets/ChevronDownIcon'
import {HotelsIcon} from './assets/HotelsIcon'
import {
  CUSTOM_HEADER_HEIGHT,
  CUSTOM_HEADER_HEIGHT_MOBILE,
  KIWI_FONT_STACK
} from './constants'
import {KiwiUserWidget} from './KiwiUserWidget'
import {navItemsMessages} from './messages'
import {MobileNavSheet} from './MobileNavSheet'
import {
  getKiwiHomepageUrl,
  getKiwiLanguageCode,
  getNavItemLinkProps,
  getNavItems
} from './navItems'

const circularFontFaces = css`
  @font-face {
    font-family: 'Circular Pro';
    src:
      local('Circular Pro Medium'),
      local('CircularPro-Medium'),
      url('https://static-data.kiwi.com/fonts/circular-pro/medium.woff2')
        format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
    font-feature-settings: normal;
    font-variation-settings: normal;
  }
`

const HeaderWrapper = styled.header`
  --custom-header-height: ${CUSTOM_HEADER_HEIGHT_MOBILE}px;
  background-color: transparent;
  width: 100%;
  z-index: 4;

  ${linariaMq.desktopXs} {
    --custom-header-height: ${CUSTOM_HEADER_HEIGHT}px;
    position: relative;
    background-color: ${cssTheme.colors.background.neutral.primary};
    box-shadow: rgba(27, 50, 73, 0.08) 0px 2px 12px 0px;
  }
`

const Nav = styled.nav`
  display: flex;
  height: ${CUSTOM_HEADER_HEIGHT_MOBILE}px;
  align-items: center;
  padding: 0 ${cssTheme.layout.spacing.s400};
  box-sizing: border-box;

  ${linariaMq.desktopXs} {
    height: ${CUSTOM_HEADER_HEIGHT}px;
    padding: 0 ${cssTheme.layout.spacing.s600};
  }
`

const LogoLink = styled.a`
  display: flex;
  align-items: center;
  height: 100%;
  text-decoration: none;
  color: inherit;
  flex-shrink: 0;
`

const DesktopNav = styled.div`
  display: none;
  align-items: center;
  height: 100%;
  margin-left: 22px;
  gap: ${cssTheme.layout.spacing.s500};

  ${linariaMq.desktopXs} {
    display: flex;
  }
`

const NavLink = styled.a`
  display: flex;
  align-items: center;
  height: 100%;
  text-decoration: none;
  font-family: ${KIWI_FONT_STACK};
  font-size: 15px;
  line-height: 15px;
  font-weight: 500;
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
  color: ${cssTheme.colors.content.neutral.primary};
  box-sizing: border-box;
  white-space: nowrap;
  transition: all cubic-bezier(0.4, 0, 0.2, 1) 0.15s;
  &.--is-active {
    color: ${cssTheme.colors.content.brand.primary};
  }

  &:hover,
  &:focus {
    color: ${cssTheme.colors.content.brand.primary};
    text-decoration: none;
    outline: none;
  }
`

const RightSection = styled.div`
  display: none;
  align-items: center;
  margin-left: auto;
  height: 100%;

  ${linariaMq.desktopXs} {
    display: flex;
  }
`

const MobileNavTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s100};
  background: none;
  border: none;
  cursor: pointer;
  padding: ${cssTheme.layout.spacing.s200} ${cssTheme.layout.spacing.s300};
  color: ${cssTheme.colors.content.neutral.primary};
  margin-left: ${cssTheme.layout.spacing.s400};

  ${linariaMq.desktopXs} {
    display: none;
  }
`

const MobileMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-right: ${minus(cssTheme.layout.spacing.s250)};

  ${linariaMq.desktopXs} {
    display: none;
  }
`

interface Props {
  currencySelector?: React.ReactNode
  /** Callback to open the preferences overlay when the mobile signed-in pill is clicked */
  onShowPreferences?: () => void
  /** When set, appends a `vio_click_id` parameter to every header link (GHA click attribution) */
  vioClickId?: string
  /** Whether the hotels nav item is the current section. Default is `true` */
  isHotelsActive?: boolean
}

export const KiwiHeader = ({
  currencySelector,
  onShowPreferences,
  vioClickId,
  isHotelsActive = true
}: Props) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const {isMobile} = useDeviceLayout()
  const {brand} = useBrand()
  const {locale} = useIntl()
  const kiwiLang = getKiwiLanguageCode(locale)
  const kiwiHomepageUrl = getKiwiHomepageUrl(kiwiLang, vioClickId)
  const hotelsHref = isHotelsActive ? undefined : '/'
  const navItems = useMemo(
    () => getNavItems(kiwiLang, vioClickId, hotelsHref),
    [kiwiLang, vioClickId, hotelsHref]
  )

  return (
    <>
      <Global styles={circularFontFaces} />
      <HeaderWrapper>
        <Nav aria-label="Main navigation">
          <LogoLink
            href={kiwiHomepageUrl}
            aria-label="Kiwi.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <BrandLogo brand={brand} height={40} compact={isMobile} />
          </LogoLink>

          <MobileNavTrigger
            onClick={() => setIsMobileNavOpen(true)}
            aria-label="Open navigation"
          >
            <HotelsIcon size={20} />
            <ChevronDownIcon size={16} />
          </MobileNavTrigger>

          <DesktopNav>
            {navItems.map(item =>
              item.isActive ? (
                <NavLink key={item.id} as="span" className="--is-active">
                  <FormattedMessageWrapper {...navItemsMessages[item.id]} />
                </NavLink>
              ) : (
                <NavLink key={item.id} {...getNavItemLinkProps(item)}>
                  <FormattedMessageWrapper {...navItemsMessages[item.id]} />
                </NavLink>
              )
            )}
          </DesktopNav>

          <RightSection>
            <Box
              display="flex"
              alignItems="center"
              paddingRight="s250"
              paddingLeft="s250"
              paddingTop="s400"
              paddingBottom="s400"
            >
              {currencySelector}
            </Box>
            <KiwiUserWidget onShowPreferences={onShowPreferences} />
          </RightSection>

          <MobileMenuWrapper>
            <KiwiUserWidget onShowPreferences={onShowPreferences} />
          </MobileMenuWrapper>
        </Nav>
      </HeaderWrapper>
      <MobileNavSheet
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        navItems={navItems}
      />
    </>
  )
}
